import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";

import { DeviceRouteLoadingDialog } from "~/components/devices/device-route-loading-dialog";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { convexClient } from "~/integrations/convex/provider";
import { toConvexTableId } from "~/lib/convex-table-id";
import { logger } from "~/lib/logger";
import { ProductEvents } from "~/lib/posthog";
import { paymentTypeOptions } from "~/lib/validators/pos-device-payment-terminal";

const getDeviceByIdRef = api.posDevices.getDeviceById;

const deleteTerminalRef = api.posDevicePaymentTerminal.deleteTerminal;

// V4.1: Route-based modal for URL context tracking
export const Route = createFileRoute(
  "/_authenticated/locations/$id/devices/$deviceId/remove-terminal"
)({
  component: RemoveTerminalModal,
});

function RemoveTerminalModal() {
  const { id: locationId, deviceId } = Route.useParams();
  const navigate = useNavigate();

  const { data: device, isPending } = useQuery({
    queryKey: ["posDevices", "getDeviceById", deviceId],
    queryFn: () => {
      return convexClient.query(getDeviceByIdRef, {
        id: toConvexTableId("posDevices", deviceId),
      });
    },
  });

  const deleteTerminalMutation = useMutation({
    mutationFn: (data: {
      posDevicePaymentTerminalId: Id<"posDevicePaymentTerminals">;
    }) => {
      return convexClient.mutation(deleteTerminalRef, data);
    },
    onSuccess: () => {
      ProductEvents.terminalRemoved(deviceId);
    },
  });

  const handleClose = () => {
    void navigate({
      to: "/locations/$id/devices/$deviceId",
      params: { id: locationId, deviceId },
    });
  };

  const handleRemove = async () => {
    if (!device?.paymentTerminal) {
      return;
    }

    try {
      await deleteTerminalMutation.mutateAsync({
        posDevicePaymentTerminalId: device.paymentTerminal._id,
      });
      toast.success("Terminal removed", {
        description: "Payment terminal has been removed successfully",
      });
      handleClose();
    } catch (error) {
      logger.exception("Error removing terminal", error);
      toast.error("Failed to remove terminal", {
        description: "Please try again.",
      });
    }
  };

  if (isPending || !device) {
    return <DeviceRouteLoadingDialog onOpenChange={handleClose} />;
  }

  if (!device.paymentTerminal) {
    return (
      <Dialog onOpenChange={handleClose} open>
        <DialogContent>
          <div className="flex items-center justify-center py-8">
            <p className="text-muted-foreground">
              No payment terminal configured
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const integration = device.paymentTerminal.type;

  return (
    <Dialog onOpenChange={handleClose} open>
      <DialogContent className="max-w-md">
        <DialogHeader className="space-y-1">
          <DialogTitle className="h2 font-heading text-foreground text-center leading-9">
            Remove Payment Terminal
          </DialogTitle>
          <DialogDescription className="body text-foreground text-center">
            This disables card payments for this POS device until another
            payment terminal is connected.
          </DialogDescription>
        </DialogHeader>
        <div className="bg-muted flex w-full space-x-3 rounded-2xl px-4 py-3">
          <img
            alt={`${paymentTypeOptions[integration].title} logo`}
            height={24}
            src={paymentTypeOptions[integration].logo}
            width={42}
          />
          <div>
            <p className="label text-foreground leading-tight">
              {paymentTypeOptions[integration].title}
            </p>
            <p className="body-sm text-foreground leading-tight">
              {device.name}
            </p>
          </div>
        </div>
        <p className="body text-foreground text-center">
          Switching terminals from the same provider? You can update terminal
          details instead.
        </p>
        <DialogFooter className="w-full space-x-3">
          <Button
            className="w-full"
            onClick={handleClose}
            type="button"
            variant="outline"
          >
            Cancel
          </Button>
          <Button
            className="w-full"
            disabled={deleteTerminalMutation.isPending}
            onClick={handleRemove}
            type="button"
            variant="destructive"
          >
            Remove Payment Terminal
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
